import { faRetweet } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormEvent, useEffect, useReducer, useState } from 'react';
import { Alert, Badge, Button, Container, Form, InputGroup, Modal } from 'react-bootstrap';
import { useFirebase } from '../../../contexts/FirebaseContext';
import { Copyable } from '../../components/Copyable/Copyable';
import { CountryFlag } from '../../components/CountryFlag/CountryFlag';
import { Footer } from '../../components/Footer/Footer';
import { Title } from '../../components/Title/Title';
import { Topbar } from '../../components/Topbar/Topbar';
import { formReducer, getVideoDuration, iso2to1 } from '../../utils/utils';

const ReplaceComponent = () => {
  const { database, getAwsGuid, manageCache } = useFirebase();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useReducer(formReducer, {
    guid: '',
  });
  const [newGuid, setNewGuid] = useState(null);
  const [error, setError] = useState(null);

  const [fetchGuid, setFetchGuid] = useState('');
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [showCheckDurationModal, setShowCheckDurationModal] = useState(false);
  const [checkDurationError, setCheckDurationError] = useState<string>(null);
  const [availableAudioLanguages, setAvailableAudioLanguages] = useState<string[]>(null);
  const [availableAudioLanguagesLoading, setAvailableAudioLanguagesLoading] = useState(false);

  useEffect(() => {
    if (!formData?.json?.egressEndpoints?.HLS) {
      setAvailableAudioLanguages(null);
      return;
    }

    setAvailableAudioLanguagesLoading(true);
    fetch(formData.json.egressEndpoints.HLS)
      .then((res) => res.text())
      .then((manifest) => {
        const rows = manifest.split(/\r?\n/);
        const audioRows = rows.filter((row) => row.startsWith('#EXT-X-MEDIA:TYPE=AUDIO'));
        const audioLanguages = audioRows.map((row) => {
          const [, language] = row.match(/NAME="(.+?)"/);
          return language;
        });
        const availableAudioLanguages = Array.from(new Set(audioLanguages));
        setAvailableAudioLanguages(availableAudioLanguages);
      })
      .catch(console.error)
      .finally(() => {
        setAvailableAudioLanguagesLoading(false);
      });
  }, [formData?.json?.egressEndpoints?.HLS]);

  useEffect(() => {
    if (!showCheckDurationModal) {
      setCheckDurationError(null);
      return;
    }

    getVideoDuration(formData.json.egressEndpoints.MSS).then((duration) => {
      if (!duration) {
        setCheckDurationError('MSS non valido, attendi qualche minuto o richiedi un nuovo render!');
        return;
      }
      setShowCheckDurationModal(false);
    });
  }, [showCheckDurationModal]);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { guid, json } = formData;
    setLoading(true);
    const videoData = {
      duration: await getVideoDuration(json.egressEndpoints.MSS),
      mss: json.egressEndpoints.MSS,
      hls: json.egressEndpoints.HLS,
      hls_drm: json.egressEndpoints.HLS_DRM,
      dash: json.egressEndpoints.DASH,
      dash_drm: json.egressEndpoints.DASH_DRM,
      mediaPackageResourceId: json.mediaPackageResourceId,
      languages: availableAudioLanguages.map(iso2to1),
      updatedGuid: json.guid,
    };
    const existingDoc = await database.doc(`videos/${guid}`).get();
    if (!existingDoc.exists) {
      setError(`Nessun video trovato corrispondente al GUID: ${guid}`);
      setLoading(false);
      return;
    }
    await manageCache('DEL_REDIS_VIDEO_HASH_KEY', guid)();
    await database.doc(`videos/${guid}`).set(videoData, { merge: true });
    const videoNotifications: { [key: string]: any }[] = await database
      .collection(`videoNotifications`)
      .where('videoGuid', '==', guid)
      .get()
      .then((data) =>
        data.docs.map((doc) => {
          return { id: doc.id, ...doc.data() };
        })
      );
    for (const lang of availableAudioLanguages.map(iso2to1)) {
      const notif = videoNotifications.find((x) => x.language == lang);
      if (!notif) {
        await database.collection('videoNotifications').add({ language: lang, scheduled: null, sent: false, videoGuid: guid });
      }
    }
    setLoading(false);
    setNewGuid(json.guid);
  };

  return (
    <>
      <Title>Sostituisci video</Title>

      <div style={{ marginBottom: '72px' }}>
        <div style={{ backgroundColor: '#141414', position: 'fixed', top: '0', left: '0', right: '0', zIndex: 100 }}>
          <Topbar />
        </div>
      </div>

      <Container className={'my-5'}>
        <Form onSubmit={onSubmit}>
          <div className={'d-flex align-items-baseline justify-content-between'}>
            <div className={'display-4'}>Sostituisci video</div>
            {!!formData.json && (
              <Button
                variant={'warning'}
                type={'submit'}
                disabled={
                  !formData.json.guid ||
                  !formData.json.egressEndpoints?.MSS ||
                  !formData.json.egressEndpoints?.HLS ||
                  !formData.json.egressEndpoints?.HLS_DRM ||
                  !formData.json.egressEndpoints?.DASH ||
                  !formData.json.egressEndpoints?.DASH_DRM ||
                  !formData.json.mediaPackageResourceId ||
                  !formData.guid
                }
              >
                <FontAwesomeIcon icon={faRetweet} className={'mr-2'} />
                Sostituisci
              </Button>
            )}
          </div>

          <div className={'mt-3 d-flex justify-content-center'}>
            <InputGroup size={'lg'} style={{ width: '100%', maxWidth: '750px' }}>
              <InputGroup.Prepend>
                <Button variant={!!formData?.json ? 'success' : 'danger'} style={{ pointerEvents: 'none' }}>
                  GUID:
                </Button>
              </InputGroup.Prepend>
              <Form.Control
                isValid={!!formData?.json}
                isInvalid={!formData?.json && fetchError}
                placeholder={'Inserisci il GUID del nuovo video...'}
                disabled={fetchLoading || formData?.json}
                value={fetchGuid}
                onKeyDown={(e) => {
                  if (e.key == 'Enter') {
                    e.preventDefault();
                    if (fetchLoading || !fetchGuid || !/^\s*[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\s*$/.test(fetchGuid)) return false;
                    (document.querySelector('#guid-submit-button') as HTMLButtonElement)?.click();
                  }
                }}
                onChange={(e) => {
                  setFetchError(false);
                  setFetchGuid(e.target.value.trim());
                }}
              />
              {!!fetchGuid && !/^\s*[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\s*$/.test(fetchGuid) && (
                <Form.Text style={{ position: 'absolute', top: '42px', left: '88px' }} className={'text-warning'}>
                  Il GUID inserito non è nel formato corretto.
                </Form.Text>
              )}
              {!formData?.json && (
                <InputGroup.Append>
                  <Button
                    disabled={fetchLoading || !fetchGuid || !/^\s*[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\s*$/.test(fetchGuid)}
                    variant={'primary'}
                    id={'guid-submit-button'}
                    onClick={() => {
                      setFetchLoading(true);
                      setFetchError(false);
                      getAwsGuid(fetchGuid).then((res) => {
                        if (!res) {
                          setFetchError(true);
                          setFetchLoading(false);
                        } else {
                          setFormData({ name: 'json', value: res });
                          setShowCheckDurationModal(true);
                        }
                      });
                    }}
                  >
                    {fetchLoading ? <>Attendi&hellip;</> : <>Ottieni dati&hellip;</>}
                  </Button>
                </InputGroup.Append>
              )}
            </InputGroup>
          </div>

          {!!formData.json && (
            <div className={'mt-3 text-center'}>
              <strong>Audio disponibili:</strong>
              {availableAudioLanguagesLoading ? (
                <span className={'ml-1'}>Caricamento&hellip;</span>
              ) : (
                <>
                  {!availableAudioLanguages?.length && (
                    <Badge className={'ml-1'} variant={'danger'}>
                      Errore!
                    </Badge>
                  )}
                  {availableAudioLanguages &&
                    availableAudioLanguages.map((language) => (
                      <CountryFlag variant={'success'} className={'ml-1'} key={language} language={language} withTooltip={true} />
                    ))}
                </>
              )}
            </div>
          )}

          {!!formData.json && !availableAudioLanguagesLoading && (
            <div className={'mt-3 d-flex justify-content-center'}>
              <Form.Group style={{ width: '100%', maxWidth: '750px' }}>
                <Form.Control
                  placeholder={'GUID da sostituire *'}
                  size={'lg'}
                  isInvalid={!formData.guid}
                  isValid={!!formData.guid}
                  required
                  value={formData.guid}
                  onChange={(e) => setFormData({ name: 'guid', value: e.target.value })}
                />
              </Form.Group>
            </div>
          )}
        </Form>
      </Container>

      <Footer />

      <Modal show={error} onHide={() => setError(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Si è verificato un errore</Modal.Title>
        </Modal.Header>
        <Modal.Body>{error}</Modal.Body>
      </Modal>

      <Modal
        show={!!newGuid}
        onHide={() => {
          setNewGuid(null);
          window.location.reload();
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Operazione completata</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Assicurarsi di verificare il corretto funzionamento nel nuovo video e di aggiornare il Mapping del video con GUID <Copyable text={formData.guid} />{' '}
          affiancandogli il nuovo GUID <Copyable text={newGuid} />.
        </Modal.Body>
      </Modal>

      <Modal
        show={showCheckDurationModal}
        onHide={() => {
          setShowCheckDurationModal(false);
          setAvailableAudioLanguagesLoading(true);
          fetch(formData.json.egressEndpoints.HLS)
            .then((res) => res.text())
            .then((manifest) => {
              const rows = manifest.split(/\r?\n/);
              const audioRows = rows.filter((row) => row.startsWith('#EXT-X-MEDIA:TYPE=AUDIO'));
              const audioLanguages = audioRows.map((row) => {
                const [, language] = row.match(/NAME="(.+?)"/);
                return language;
              });
              const availableAudioLanguages = Array.from(new Set(audioLanguages));
              setAvailableAudioLanguages(availableAudioLanguages);
            })
            .catch(console.error)
            .finally(() => {
              setAvailableAudioLanguagesLoading(false);
            });
        }}
        backdrop={'static'}
        centered
        keyboard={false}
      >
        <Modal.Header>
          <Modal.Title>Controllo video&hellip;</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!checkDurationError && <p>Attendere&hellip;</p>}
          {!!checkDurationError && (
            <Alert variant={'danger'} className={'mb-0'}>
              {checkDurationError}
            </Alert>
          )}
        </Modal.Body>
        {!!checkDurationError && (
          <Modal.Footer>
            <Button variant={'secondary'} onClick={() => window.location.reload()}>
              Ricarica
            </Button>
          </Modal.Footer>
        )}
      </Modal>
    </>
  );
};

export default ReplaceComponent;
